<template>
  <el-container>
    <el-main>
      <div style="margin-bottom: 16px">
        <el-breadcrumb separator-class="el-icon-arrow-right">
          <el-breadcrumb-item class="item_font">系统设置</el-breadcrumb-item>
          <el-breadcrumb-item>
            <span class="item_font">智能助手配置</span>
          </el-breadcrumb-item>
        </el-breadcrumb>
      </div>

      <div class="toolbar">
        <div class="toolbar-left">
          <el-button type="primary" size="small" @click="openEdit()">新增配置</el-button>
          <el-button size="small" @click="openGlobalPrompt">全局更新 Prompt</el-button>
          <el-button size="small" @click="loadList">刷新</el-button>
        </div>
        <div class="toolbar-right" v-if="checkedList.length">
          当前启用：
          <el-tag
            v-for="item in checkedList"
            :key="item.id"
            size="small"
            type="success"
            style="margin-left: 6px"
          >
            {{ item.modelName }}{{ item.modelVersion ? ` / ${item.modelVersion}` : "" }}
          </el-tag>
        </div>
      </div>

      <el-table
        v-loading="loading"
        :data="tableData"
        stripe
        border
        style="width: 100%"
        :header-cell-style="headerStyle"
        :max-height="tableMaxHeight"
      >
        <el-table-column prop="modelName" label="模型名称" min-width="120" show-overflow-tooltip />
        <el-table-column prop="modelVersion" label="模型版本" min-width="100" show-overflow-tooltip />
        <el-table-column label="Prompt 模板" min-width="220">
          <template slot-scope="scope">
            <el-tooltip
              v-if="scope.row.promptTemplate"
              effect="dark"
              placement="top"
              :content="scope.row.promptTemplate"
            >
              <span class="prompt-preview">{{ scope.row.promptTemplate }}</span>
            </el-tooltip>
            <span v-else class="prompt-empty">未配置</span>
          </template>
        </el-table-column>
        <el-table-column prop="priority" label="优先级" width="90" align="center" />
        <el-table-column label="启用" width="100" align="center">
          <template slot-scope="scope">
            <el-switch
              :value="Number(scope.row.checked) === 1"
              :disabled="switchingId === scope.row.id"
              @change="(val) => onCheckedChange(scope.row, val)"
            />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="160" fixed="right" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="openEdit(scope.row)">编辑</el-button>
            <el-button type="text" size="small" style="color: #f56c6c" @click="onDelete(scope.row)"
              >删除</el-button
            >
          </template>
        </el-table-column>
      </el-table>

      <!-- 新增 / 编辑 -->
      <el-dialog
        :title="form.id ? '编辑智能助手配置' : '新增智能助手配置'"
        :visible.sync="editVisible"
        width="640px"
        :close-on-click-modal="false"
        @closed="resetForm"
      >
        <el-form ref="editForm" :model="form" :rules="rules" label-width="110px">
          <el-form-item label="模型名称" prop="modelName">
            <el-input v-model.trim="form.modelName" maxlength="100" placeholder="请输入模型名称" />
          </el-form-item>
          <el-form-item label="模型版本" prop="modelVersion">
            <el-input
              v-model.trim="form.modelVersion"
              maxlength="100"
              placeholder="请输入模型版本"
            />
          </el-form-item>
          <el-form-item label="优先级" prop="priority">
            <el-input-number v-model="form.priority" :min="0" :max="9999" controls-position="right" />
          </el-form-item>
          <el-form-item label="启用" prop="checked">
            <el-switch
              v-model="form.checked"
              :active-value="1"
              :inactive-value="0"
              active-text="启用"
              inactive-text="停用"
            />
          </el-form-item>
          <el-form-item label="Prompt 模板" prop="promptTemplate">
            <el-input
              v-model="form.promptTemplate"
              type="textarea"
              :rows="8"
              placeholder="请输入 Prompt 模板"
            />
          </el-form-item>
        </el-form>
        <span slot="footer">
          <el-button @click="editVisible = false">取消</el-button>
          <el-button type="primary" :loading="saving" @click="submitForm">保存</el-button>
        </span>
      </el-dialog>

      <!-- 全局 Prompt -->
      <el-dialog
        title="全局更新 Prompt"
        :visible.sync="globalVisible"
        width="640px"
        :close-on-click-modal="false"
      >
        <el-alert
          title="将使用下方 Prompt 覆盖所有已配置模型的提示词"
          type="warning"
          :closable="false"
          show-icon
          style="margin-bottom: 12px"
        />
        <el-input
          v-model="globalPrompt"
          type="textarea"
          :rows="10"
          placeholder="请输入要全局更新的 Prompt"
        />
        <span slot="footer">
          <el-button @click="globalVisible = false">取消</el-button>
          <el-button type="primary" :loading="globalSaving" @click="submitGlobalPrompt"
            >确认更新</el-button
          >
        </span>
      </el-dialog>
    </el-main>
  </el-container>
</template>

<script>
import { maxHeight } from "../../utils/tableHeight";

const API = {
  list: "/manager/intelligentAssistantConfig/list",
  checkedList: "/manager/intelligentAssistantConfig/getCheckedList",
  save: "/manager/intelligentAssistantConfig/save",
  delete: "/manager/intelligentAssistantConfig/delete",
  setChecked: "/manager/intelligentAssistantConfig/setChecked",
  updateGlobalPrompt: "/manager/intelligentAssistantConfig/updateGlobalPrompt",
};

export default {
  name: "IntelligentAssistant",
  data() {
    return {
      loading: false,
      saving: false,
      globalSaving: false,
      switchingId: "",
      tableData: [],
      checkedList: [],
      editVisible: false,
      globalVisible: false,
      globalPrompt: "",
      form: {
        id: "",
        modelName: "",
        modelVersion: "",
        promptTemplate: "",
        checked: 0,
        priority: 0,
      },
      rules: {
        modelName: [{ required: true, message: "请输入模型名称", trigger: "blur" }],
        promptTemplate: [{ required: true, message: "请输入 Prompt 模板", trigger: "blur" }],
      },
    };
  },
  computed: {
    tableMaxHeight() {
      try {
        return maxHeight();
      } catch (e) {
        return 560;
      }
    },
  },
  created() {
    this.loadAll();
  },
  methods: {
    headerStyle() {
      return {
        background: "#f5f7fa",
        color: "#333",
        fontWeight: 600,
      };
    },
    isOk(res) {
      return res && String(res.resultCode) === "10000";
    },
    async loadAll() {
      await Promise.all([this.loadList(), this.loadCheckedList()]);
    },
    async loadList() {
      this.loading = true;
      try {
        const res = await this.util.postRequest({
          url: API.list,
          method: "POST",
        });
        if (this.isOk(res)) {
          this.tableData = Array.isArray(res.data) ? res.data : [];
        } else {
          this.$message.error((res && res.desc) || "获取配置列表失败");
        }
      } catch (e) {
        console.error(e);
        this.$message.error("获取配置列表失败");
      } finally {
        this.loading = false;
      }
    },
    async loadCheckedList() {
      try {
        const res = await this.util.postRequest({
          url: API.checkedList,
          method: "POST",
        });
        if (this.isOk(res)) {
          this.checkedList = Array.isArray(res.data) ? res.data : [];
        }
      } catch (e) {
        console.error(e);
      }
    },
    resetForm() {
      this.form = {
        id: "",
        modelName: "",
        modelVersion: "",
        promptTemplate: "",
        checked: 0,
        priority: 0,
      };
      if (this.$refs.editForm) {
        this.$refs.editForm.clearValidate();
      }
    },
    openEdit(row) {
      if (row) {
        this.form = {
          id: row.id || "",
          modelName: row.modelName || "",
          modelVersion: row.modelVersion || "",
          promptTemplate: row.promptTemplate || "",
          checked: Number(row.checked) === 1 ? 1 : 0,
          priority: Number(row.priority) || 0,
        };
      } else {
        this.resetForm();
      }
      this.editVisible = true;
    },
    submitForm() {
      this.$refs.editForm.validate(async (valid) => {
        if (!valid) return;
        this.saving = true;
        try {
          const payload = {
            id: this.form.id || undefined,
            modelName: this.form.modelName,
            modelVersion: this.form.modelVersion,
            promptTemplate: this.form.promptTemplate,
            checked: Number(this.form.checked) === 1 ? 1 : 0,
            priority: Number(this.form.priority) || 0,
          };
          const res = await this.util.postRequest({
            url: API.save,
            method: "POST",
            data: payload,
          });
          if (this.isOk(res)) {
            this.$message.success(this.form.id ? "更新成功" : "新增成功");
            this.editVisible = false;
            await this.loadAll();
          } else {
            this.$message.error((res && res.desc) || "保存失败");
          }
        } catch (e) {
          console.error(e);
          this.$message.error("保存失败");
        } finally {
          this.saving = false;
        }
      });
    },
    async onCheckedChange(row, enabled) {
      const checked = enabled ? 1 : 0;
      this.switchingId = row.id;
      try {
        const res = await this.util.postRequest({
          url: `${API.setChecked}?id=${encodeURIComponent(row.id)}&checked=${checked}`,
          method: "POST",
        });
        if (this.isOk(res)) {
          this.$message.success(checked === 1 ? "已启用" : "已停用");
          row.checked = checked;
          await this.loadCheckedList();
        } else {
          this.$message.error((res && res.desc) || "更新启用状态失败");
        }
      } catch (e) {
        console.error(e);
        this.$message.error("更新启用状态失败");
      } finally {
        this.switchingId = "";
      }
    },
    onDelete(row) {
      this.$confirm(`确定删除模型「${row.modelName || row.id}」吗？`, "提示", {
        type: "warning",
        confirmButtonText: "删除",
        cancelButtonText: "取消",
      })
        .then(async () => {
          try {
            const res = await this.util.postRequest({
              url: `${API.delete}?id=${encodeURIComponent(row.id)}`,
              method: "POST",
            });
            if (this.isOk(res)) {
              this.$message.success("删除成功");
              await this.loadAll();
            } else {
              this.$message.error((res && res.desc) || "删除失败");
            }
          } catch (e) {
            console.error(e);
            this.$message.error("删除失败");
          }
        })
        .catch(() => {});
    },
    openGlobalPrompt() {
      const first = this.tableData.find((item) => item.promptTemplate) || this.tableData[0];
      this.globalPrompt = (first && first.promptTemplate) || "";
      this.globalVisible = true;
    },
    async submitGlobalPrompt() {
      if (!this.globalPrompt || !String(this.globalPrompt).trim()) {
        this.$message.warning("请输入 Prompt 内容");
        return;
      }
      this.globalSaving = true;
      try {
        const res = await this.util.postRequest({
          url: API.updateGlobalPrompt,
          method: "POST",
          data: {
            promptTemplate: this.globalPrompt,
          },
        });
        if (this.isOk(res)) {
          this.$message.success("全局 Prompt 已更新");
          this.globalVisible = false;
          await this.loadAll();
        } else {
          this.$message.error((res && res.desc) || "全局更新失败");
        }
      } catch (e) {
        console.error(e);
        this.$message.error("全局更新失败");
      } finally {
        this.globalSaving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}

.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.toolbar-right {
  @include change_size($font_size_normal);
  color: #606266;
}

.prompt-preview {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #303133;
}

.prompt-empty {
  color: #c0c4cc;
}

.item_font {
  @include change_size($font_size_normal);
}
</style>
